import { BOARD_LIMITS, isUuid, newAssetId, type SceneStore } from '@os/doc';
import { GIF_ORIGINAL_MAX, imageDimensions, imageKind, MAX_IMAGE_PIXELS } from '@os/shared';
import type { Point } from '@os/engine';
import { createWorkerPool } from './pool';
import type { AssetMeta, LocalMediaStore, VariantBlobs } from './store';
import { GIF_VARIANTS, STILL_VARIANTS } from './variants';

export const MAX_IMAGE_BYTES = 40 * 1024 * 1024;
export const MAX_FILES = 100;
export const MAX_PIXELS = MAX_IMAGE_PIXELS;
/** Magic bytes and header size readers, shared with the Worker's upload checks (P12a). */
export { imageDimensions, imageKind as sniffImage };
const CONCURRENCY = 3;
const DISPLAY_LONG_EDGE = 480;
const GAP = 24;

export type ImportFailure = { name: string; reason: 'too-large' | 'dimensions' | 'svg' | 'unsupported' | 'corrupt' | 'storage' | 'too-many' | 'board-full' };
export type Processed = { w: number; h: number; thumbhash: string; variants: VariantBlobs };
/**
 * Decode + variants + thumbhash. Runs in a Web Worker in the app; injected in tests (happy-dom cannot decode).
 * `posterOnly`: 1024 and 256 only (a GIF whose original is kept).
 */
export type ProcessImage = (file: File, opts?: { posterOnly: boolean }) => Promise<Processed>;

class ImportError extends Error {
  constructor(readonly reason: ImportFailure['reason']) {
    super(reason);
  }
}

/** SVG can carry scripts: refused whatever its declared type or extension (any text starting with '<'). */
function looksLikeSvg(file: File, head: Uint8Array): boolean {
  if (file.type === 'image/svg+xml' || /\.svgz?$/i.test(file.name)) return true;
  const text = new TextDecoder().decode(head).trimStart();
  return text.startsWith('<');
}

/** Without crypto.subtle (plain HTTP outside localhost, e.g. a phone on the LAN) dedupe is skipped, import still works. */
const canHash = () => typeof crypto !== 'undefined' && !!crypto.subtle;

const claimsImage = (f: File) => f.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|avif|heic|heif)$/i.test(f.name);

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((x) => x.toString(16).padStart(2, '0')).join('');
}

async function mapLimit<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]!);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return out;
}

type Size = { w: number; h: number };
type Box = Size & { x: number; y: number };

/** Rows of ceil(sqrt(n)) images (long edge ≤ 480 px), gap 24 px, the whole grid centred on `at`. */
export function gridLayout(sizes: Size[], at: Point): Box[] {
  const display = sizes.map((s) => {
    const k = Math.min(1, DISPLAY_LONG_EDGE / Math.max(s.w, s.h));
    return { w: Math.max(1, Math.round(s.w * k)), h: Math.max(1, Math.round(s.h * k)) };
  });
  const cols = Math.max(1, Math.ceil(Math.sqrt(display.length)));
  const cellW = Math.max(0, ...display.map((d) => d.w));
  const boxes: Box[] = [];
  let top = 0;
  for (let r = 0; r * cols < display.length; r++) {
    const row = display.slice(r * cols, (r + 1) * cols);
    row.forEach((d, c) => boxes.push({ ...d, x: c * (cellW + GAP), y: top }));
    top += Math.max(...row.map((d) => d.h)) + GAP;
  }
  if (!boxes.length) return boxes;
  const minX = Math.min(...boxes.map((b) => b.x));
  const maxX = Math.max(...boxes.map((b) => b.x + b.w));
  const minY = Math.min(...boxes.map((b) => b.y));
  const maxY = Math.max(...boxes.map((b) => b.y + b.h));
  const dx = at.x - (minX + maxX) / 2;
  const dy = at.y - (minY + maxY) / 2;
  return boxes.map((b) => ({ ...b, x: b.x + dx, y: b.y + dy }));
}

/**
 * Validates, deduplicates (sha256), processes and stores each file, then creates one image object per success.
 * A file that fails leaves no trace: no stored blob, no object.
 */
/** The organization already has this content (server boards): its id and pixel size, so nothing is decoded. */
export type CheckAsset = (sha256: string) => Promise<{ assetId: string; w: number; h: number } | null>;

/** `adopt`: runs on the new images inside their undo step (the editor attaches them to the section under them). */
export type ImportOptions = { process?: ProcessImage; check?: CheckAsset; onStored?: (meta: AssetMeta, name: string) => void; adopt?: (ids: string[]) => void };

/** Validates, deduplicates, processes and stores files as assets, without placing anything on the board. */
export async function importAssets(files: File[], media: LocalMediaStore, opts: ImportOptions = {}): Promise<{ assets: AssetMeta[]; failed: ImportFailure[] }> {
  const process = opts.process ?? processInWorker;
  const failed: ImportFailure[] = files.slice(MAX_FILES).map((f) => ({ name: f.name, reason: 'too-many' }));
  const pending = new Map<string, Promise<AssetMeta>>(); // same content twice in one drop: processed once

  const prepare = async (file: File): Promise<AssetMeta | ImportFailure> => {
    try {
      if (file.size > MAX_IMAGE_BYTES) throw new ImportError('too-large');
      const head = new Uint8Array(await file.slice(0, 65536).arrayBuffer());
      if (looksLikeSvg(file, head.subarray(0, 64))) throw new ImportError('svg');
      const kind = imageKind(head);
      if (!kind) throw new ImportError(claimsImage(file) ? 'corrupt' : 'unsupported');
      const dims = imageDimensions(head, kind);
      if (dims && dims.w * dims.h > MAX_PIXELS) throw new ImportError('dimensions');
      const sha = canHash() ? await sha256(file) : '';
      const known = sha ? media.findBySha(sha) : undefined;
      // On a server board only uuid assets can be uploaded: a P1 local-only asset is imported afresh.
      if (known && (!opts.check || isUuid(known.assetId))) return known;
      const remote = sha && opts.check ? await opts.check(sha).catch(() => null) : null;
      if (remote) return { ...remote, sha256: sha, thumbhash: '', mime: `image/${kind}`, expected: [] };
      let job = sha ? pending.get(sha) : undefined;
      if (!job) {
        job = (async () => {
          const keepGif = kind === 'gif' && file.size <= GIF_ORIGINAL_MAX;
          const out = await process(file, { posterOnly: keepGif }).catch(() => {
            throw new ImportError('corrupt');
          });
          const meta: AssetMeta = {
            assetId: newAssetId(), sha256: sha, w: out.w, h: out.h, thumbhash: out.thumbhash,
            mime: `image/${kind}`, expected: keepGif ? GIF_VARIANTS : STILL_VARIANTS,
          };
          const variants = keepGif ? { ...out.variants, original: file } : out.variants;
          await media.putAsset(meta, variants).catch(() => {
            throw new ImportError('storage');
          });
          opts.onStored?.(meta, file.name);
          return meta;
        })();
        if (sha) pending.set(sha, job);
      }
      return await job;
    } catch (err) {
      return { name: file.name, reason: err instanceof ImportError ? err.reason : 'corrupt' };
    }
  };

  const results = await mapLimit(files.slice(0, MAX_FILES), CONCURRENCY, prepare);
  failed.unshift(...results.filter((r): r is ImportFailure => 'reason' in r));
  return { assets: results.filter((r): r is AssetMeta => 'assetId' in r), failed };
}

export async function importImages(
  files: File[],
  at: Point,
  store: SceneStore,
  media: LocalMediaStore,
  opts: ImportOptions = {},
): Promise<{ created: string[]; failed: ImportFailure[] }> {
  // Only what the board can still hold is stored and uploaded (spec quotas, P12a); the rest is reported.
  const counts = store.counts();
  const room = Math.max(0, Math.min(BOARD_LIMITS.images - counts.images, BOARD_LIMITS.objects - counts.objects));
  const { assets, failed } = await importAssets(files.slice(0, room), media, opts);
  failed.push(...files.slice(room).map((f): ImportFailure => ({ name: f.name, reason: 'board-full' })));
  const boxes = gridLayout(assets, at);
  // One undo step for the whole drop (each command is otherwise its own step).
  store.beginGesture();
  try {
    const created = assets.map((a, i) =>
      store.commands.createObject({
        type: 'image', assetId: a.assetId, crop: { x: 0, y: 0, w: 1, h: 1 }, alt: '',
        parentId: null, ...boxes[i]!, rotation: 0, locked: false, style: {},
      }),
    );
    const placed = created.filter(Boolean); // a peer may have filled the board meanwhile
    if (placed.length) opts.adopt?.(placed);
    return { created: placed, failed };
  } finally {
    store.endGesture();
  }
}

/** Decoding/encoding pool (3 workers, created on first use); a worker silent for 2 minutes is replaced. */
export const processInWorker: ProcessImage = createWorkerPool({
  size: CONCURRENCY,
  timeoutMs: 120_000,
  spawn: () => new Worker(new URL('./pipeline.worker.ts', import.meta.url), { type: 'module' }),
});
